import LocalStorage from './localStorageHelper';
import { localStorageKeys } from '../constants/localStorageKeys';
import {
  SwiftaidLinkState,
  readGiftAidStatus,
  swiftaidLinkState
} from '../pages/organization/swiftaidConnection/constants';

/**
 * The organisation admin's own organisation, cached in localStorage under the
 * legacy `paymenInfo` key. The session reducer writes it when the general layout
 * loads the organisation record, and the layout only loads that record when this
 * cache is missing or stale, so it is the one place the admin's own record is
 * guaranteed to be reflected.
 *
 * `isConnected` keeps its historical meaning (payment gateways present) and its
 * historical looseness: it can be undefined. `giftAidStatus` is always written,
 * at minimum `unknown`, which is what tells the layout the cache is current.
 */
export interface OrgInfoCache {
  orgId: number | string | null;
  isConnected?: boolean;
  giftAidStatus: SwiftaidLinkState;
}

const cacheKey = localStorageKeys.PAYMENT_INFO;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The raw stored string. This is the snapshot handed to `useSyncExternalStore`:
 * strings compare by value, so an unchanged cache is a stable snapshot. Parsing
 * here would hand React a fresh object on every call and make it re-render forever.
 */
export const readOrgInfoRaw = (): string | null => {
  try {
    return localStorage.getItem(cacheKey);
  } catch (e) {
    return null;
  }
};

/** Tolerates corrupt JSON, non objects and a pre-upgrade cache with no status. */
export const parseOrgInfo = (raw: string | null): OrgInfoCache | null => {
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  if (!isPlainObject(parsed)) {
    return null;
  }
  const orgId = parsed.orgId;
  return {
    orgId:
      typeof orgId === 'number' || typeof orgId === 'string' ? orgId : null,
    isConnected:
      typeof parsed.isConnected === 'boolean' ? parsed.isConnected : undefined,
    giftAidStatus: readGiftAidStatus(parsed.giftAidStatus)
  };
};

export const readOrgInfo = (): OrgInfoCache | null =>
  parseOrgInfo(readOrgInfoRaw());

/**
 * Whether a cache entry was written since `giftAidStatus` was added. Checks for
 * the key rather than a truthy value so a stored `unknown` still counts as
 * current; otherwise the layout would refetch the record on every render.
 */
export const hasGiftAidStatus = (value: unknown): boolean =>
  isPlainObject(value) && 'giftAidStatus' in value;

const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach((listener) => listener());
};

export const writeOrgInfo = (info: OrgInfoCache): void => {
  LocalStorage.setItem(cacheKey, info);
  notify();
};

/**
 * Record a status freshly read from Swiftaid against the cached organisation,
 * so anything showing the cached status (the Gift Aid nudge) updates at once.
 * Leaves the cache alone when it belongs to a different organisation, which is
 * the case for a system admin viewing someone else's finance tab, and when the
 * new status is `unknown`, since a non answer must not overwrite a known one.
 */
export const updateCachedGiftAidStatus = (
  orgId: string | number | null | undefined,
  status: SwiftaidLinkState
): boolean => {
  if (orgId == null || status === swiftaidLinkState.unknown) {
    return false;
  }
  const cached = readOrgInfo();
  if (cached === null || String(cached.orgId) !== String(orgId)) {
    return false;
  }
  writeOrgInfo({ ...cached, giftAidStatus: status });
  return true;
};

// Another tab writing the cache (linking Swiftaid there, or logging out) shows
// up here as a storage event. A null key means the whole store was cleared.
const onStorage = (event: StorageEvent) => {
  if (event.key === null || event.key === cacheKey) {
    notify();
  }
};

export const subscribeOrgInfo = (listener: () => void): (() => void) => {
  if (listeners.size === 0) {
    window.addEventListener('storage', onStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('storage', onStorage);
    }
  };
};
